import { Module } from 'vuex';
import * as api from "@/api/ApiClient";
import {AuthClient} from "@/api/ApiClientBase";
import {
    ChangeSet,
    ChangeSetDTO,
    ChangeSetItemDTO,
    ChangeSetItem
} from "@/api/ApiClient";
import {IAlmFileSystemObjectContent, IChangeset, IChangesetDetails} from "@/components/pages/landscape/LandscapeManagementInterfaces";
import { getApolloClient, setGraphqlUri } from '@/apollo';
import { GET_CHANGESET_ITEMS } from '@/graphql/ALMQueries';
import { ALMNotificationsHandler } from '@/api/ALMNotificationsHandler';

interface AlmStoreInterface {
    isAlmView: boolean,
    selectedAlmChangesets: Array<IChangeset> | null,
    newChangeset: ChangeSetDTO | null,
    selectedAlmFso: api.FileSystemObjectContent | null,
    changesets: Array<IChangeset> | null,
    changesetItems: Array<IAlmFileSystemObjectContent> | null, // This will hold the changeset items fetched from the API
    changesetItemIds: Array<number> | null,
    actionableChangeset: IChangeset | null,
    ALMNotificationHandler: ALMNotificationsHandler | null;
    LastReceivedALMNotification: any | null;
    selectedAlmTargetTenant: IChangesetDetails | null;
}

const AlmStore: Module<AlmStoreInterface, any> = {
    namespaced: true,
    state: {
        isAlmView: false,
        selectedAlmChangesets: null,
        newChangeset: null,
        selectedAlmFso: null,
        changesets: null,
        changesetItems: null, // This will hold the changeset items fetched from the API
        actionableChangeset: null,
        changesetItemIds: null,
        ALMNotificationHandler: null,
        LastReceivedALMNotification: null,
        selectedAlmTargetTenant: null
    },
    getters: {
        isAlmView: (state: AlmStoreInterface) => state.isAlmView,
        selectedAlmChangesets: (state: AlmStoreInterface) => state.selectedAlmChangesets,
        newChangeset: (state: AlmStoreInterface) => state.newChangeset,
        selectedAlmFso: (state: AlmStoreInterface) => state.selectedAlmFso,
        changesets: (state: AlmStoreInterface) => state.changesets,
        selectedFiles: (_state: AlmStoreInterface, _getters, _rootState, rootGetters) => {
            const changesetItemsDTO: Array<IAlmFileSystemObjectContent> = [];
            const pushChangesetItemsDTO = (child: IAlmFileSystemObjectContent) => {
                if (child?.almChecked) {
                    changesetItemsDTO.push(child);
                } else if (child?.objectType === api.FileSystemType.Folder) {
                    child.folderContent?.forEach((fso: IAlmFileSystemObjectContent) => {
                        pushChangesetItemsDTO(fso);
                    });
                }
            };
            pushChangesetItemsDTO(rootGetters['explorer/root']);
            //sort by alphabetical order
            changesetItemsDTO?.sort((a, b) => (a.name ? a.name : '').localeCompare(b.name ? b.name : ''));
            return changesetItemsDTO;
        },
        changesetItems: (state: AlmStoreInterface) => state.changesetItems,
        actionableChangeset: (state: AlmStoreInterface) => {
            if(state.actionableChangeset) {
                return state.actionableChangeset;
            } else if(state.selectedAlmChangesets && state.selectedAlmChangesets.length === 1) {
                return state.selectedAlmChangesets[0];
            }
            return null;
        },
        changesetItemIds: (state: AlmStoreInterface) => state.changesetItemIds,
        selectedAlmTargetTenant: (state: AlmStoreInterface) => state.selectedAlmTargetTenant,
    },
    mutations: {
        setAlmView(state: AlmStoreInterface, value: boolean) {
            state.isAlmView = value;
        },
        setSelectedAlmChangesets(state: AlmStoreInterface, value: Array<IChangeset> | null) {
            state.selectedAlmChangesets = value;
            if(value?.length === 1) {
                state.actionableChangeset = value?.[0];
            } else{
                state.selectedAlmTargetTenant = null;
            }
        },
        setNewChangeset(state: AlmStoreInterface, value:ChangeSetDTO | null) {
            state.newChangeset = value;
        },
        setSelectedAlmFso(state: AlmStoreInterface, value:api.FileSystemObjectContent | null) {
            state.selectedAlmFso = value;
        },
        setChangesets(state: AlmStoreInterface, value: Array<IChangeset> | null) {
            state.changesets = value;
        },
        setChangesetItems(state: AlmStoreInterface, value: Array<IAlmFileSystemObjectContent> | null) {
            state.changesetItems = value;
        },
        setActionableChangeset(state: AlmStoreInterface, value: IChangeset | null) {
            state.actionableChangeset = value;
        },
        setChangesetItemIds(state: AlmStoreInterface, value: Array<number> | null) {
            state.changesetItemIds = value;
        },
        SetALMNotificationHandler(state, handler) {
            if (state.ALMNotificationHandler) state.ALMNotificationHandler.stop();
            state.ALMNotificationHandler = handler;
            state.ALMNotificationHandler?.start();
        },
        SetLastReceivedALMNotification(state, notification) {
            state.LastReceivedALMNotification = notification;
        },
        setSelectedAlmTargetTenant(state: AlmStoreInterface, value: IChangesetDetails | null) {
            state.selectedAlmTargetTenant = value;
        },
    },
    actions: {
        async getChangesets(context) {
            const auth_client = AuthClient.getInstance();
            auth_client.ensureToken();
      
            setGraphqlUri(`${AuthClient.configuration.apiBaseUrl}/graphql`);
            const client = getApolloClient();
            const { data } = await client.query({
                    query: GET_CHANGESET_ITEMS
                  });
            const changesetList = JSON.parse(JSON.stringify(data.landscapeManagement.changeSets.items));
            context.commit('setChangesets', changesetList);
        },
        async getChangesetItems(context) {
            const auth_client = AuthClient.getInstance();
            auth_client.ensureToken();
            const selectedChangeset = context.getters.actionableChangeset;
            if(selectedChangeset?.count > 0){
                const root = await context.dispatch('getExplorerData', selectedChangeset?.source);
                const changesetItems = await context.dispatch('fetchChangesetItems');
                const itemIds = new Set((changesetItems || []).map((item: ChangeSetItem) => item.itemId));

                function mapFolderWithChangeset(node: IAlmFileSystemObjectContent): { nodes: IAlmFileSystemObjectContent[], matchedCount: number } {
                    if (!node) return { nodes: [], matchedCount: 0 };
                    if (node.folderContent && Array.isArray(node.folderContent) && node.folderContent.length > 0) {
                        let total = node.folderContent.length;
                        let matchedNodes: IAlmFileSystemObjectContent[] = [];
                        let matchedCount = 0;
                        for (const child of node.folderContent) {
                            const result = mapFolderWithChangeset(child);
                            matchedNodes.push(...result.nodes);
                            matchedCount += result.matchedCount;
                        }
                        if (matchedCount === total && total > 0) {
                            return { nodes: [node], matchedCount: 1 };
                        } else {
                            return { nodes: matchedNodes, matchedCount };
                        }
                    } else {
                        if (itemIds.has(node.id)) {
                            return { nodes: [node], matchedCount: 1 };
                        }
                        return { nodes: [], matchedCount: 0 };
                    }
                }
                const { nodes: matchedRootObjects } = mapFolderWithChangeset(root);
                const mappedChangesetItems: IAlmFileSystemObjectContent[] = Array.from(new Set(matchedRootObjects));
                context.commit('setChangesetItems', mappedChangesetItems);
            } else {
                context.commit('setChangesetItems', []);
            }
        },
        async fetchChangesetItems(context): Promise<ChangeSetItem[]> {
            const auth_client = AuthClient.getInstance();
            auth_client.ensureToken();
            const client = new api.LandscapeManagementClient(auth_client);
            const selectedChangeset = context.getters.actionableChangeset;
            if (selectedChangeset?.count > 0) {
                const changesetItems = await client.getChangeSetItems(selectedChangeset.id);
                context.commit('setChangesetItemIds', changesetItems.map(item => item?.itemId));
                return Promise.resolve(changesetItems);
            }
            context.commit('setChangesetItemIds', []);
            return Promise.resolve([]);
        },
        async getExplorerData(context, tenantName) {
            const auth_client = AuthClient.getInstance();
            await auth_client.ensureToken();
            const client = new api.FileSystemClient(auth_client);
            const fs = await client.getFile("", null, true, tenantName);
            return fs;
        },
        async getRoot(context, tenantName) {
            await context.dispatch('explorer/LoadFileSystem', tenantName, { root:true });
        },
        async submitNewChangeset(context) {
            const auth_client = AuthClient.getInstance();
            auth_client.ensureToken();
            const client = new api.LandscapeManagementClient(auth_client);
            let changeset:ChangeSet | null = null;
                changeset = await client.createChangeSet(context.getters.newChangeset);
            const changesetItemsDTO:Array<ChangeSetItemDTO> = [];
            const pushChangesetItemsDTO = (child: IAlmFileSystemObjectContent) => {
                if (!child) {
                    return;
                }
                if (child.objectType !== api.FileSystemType.Folder) {
                    if (child.almChecked) {
                        changesetItemsDTO.push(new ChangeSetItemDTO({
                            itemId: child.id,
                            itemName: child.name,
                            itemType: `${child.objectType}`
                        }));
                    }
                } else {
                    child.folderContent?.forEach((fso: IAlmFileSystemObjectContent) => {
                        pushChangesetItemsDTO(fso);
                    })
                }
            }

            pushChangesetItemsDTO(context.rootGetters['explorer/root']);
            if (changeset && changesetItemsDTO.length) {
                await client.createChangeSetItems(changeset.id, changesetItemsDTO);
            }
            return changeset;
        },
        async updateChangeset(context, changeSetId) {
            const auth_client = AuthClient.getInstance();
            auth_client.ensureToken();
            const client = new api.LandscapeManagementClient(auth_client);
            let changeset:ChangeSet | null = null;
                changeset = await client.updateChangeSet(changeSetId, context.getters.newChangeset);
            const changesetItemsDTO:Array<ChangeSetItemDTO> = [];
            const pushChangesetItemsDTO = (child: IAlmFileSystemObjectContent) => {
                if (!child) {
                    return;
                }
                if (child.objectType !== api.FileSystemType.Folder) {
                    if (child.almChecked) {
                        changesetItemsDTO.push(new ChangeSetItemDTO({
                            itemId: child.id,
                            itemName: child.name,
                            itemType: `${child.objectType}`
                        }));
                    }
                } else {
                    child.folderContent?.forEach((fso: IAlmFileSystemObjectContent) => {
                        pushChangesetItemsDTO(fso);
                    })
                }
            }

            pushChangesetItemsDTO(context.rootGetters['explorer/root']);
            if (changeset && (changesetItemsDTO.length || context.rootGetters['explorer/root'])) {
                return await client.createChangeSetItems(changeset.id, changesetItemsDTO);
            }
            return changeset;
        },
        async deleteChangeset (context, changesetId) {
            try{
                const auth_client = AuthClient.getInstance();
                await auth_client.ensureToken();
                const client = new api.LandscapeManagementClient(auth_client);
                await client.deleteChangeSet(changesetId);
                await context.dispatch('getChangesets');
                context.commit('setSelectedAlmChangesets', null);
                this.dispatch("ShowToast", new api.ToastNotification({
                    type: api.ToastType.Info,
                    message: context.rootState.Localizer.Localize("LandscapeManagement_Changeset_Deleted")
                }));
            }
            catch (err) {
                this.dispatch("ShowToastError", err);
            }
        },
        async RunALMDeployment(context, currentChangeset) {
            try {
                const auth_client = AuthClient.getInstance();
                auth_client.ensureToken();
                const client = new api.LandscapeManagementClient(auth_client);
                
                const targetTenantIds = currentChangeset.detail?.filter((tenantDetails: IChangesetDetails) => tenantDetails?.include).map((tenantDetails: IChangesetDetails) => tenantDetails?.tenantId);
                await client.initiateDeployment(currentChangeset?.id, targetTenantIds);
        
                this.dispatch("ShowToast", new api.ToastNotification({
                    message: "ALM Deployment started",
                    type: api.ToastType.Info
                }));
                context.commit('setSelectedAlmChangesets', null);
            } catch (err) {
                this.dispatch("ShowToastError", err);
            }
        },
        async CancelALMDeployment(context, currentChangeset) {
            try {
                const auth_client = AuthClient.getInstance();
                auth_client.ensureToken();
                const client = new api.LandscapeManagementClient(auth_client);
                await client.cancelDeployment(currentChangeset?.id);
                this.dispatch("ShowToast", new api.ToastNotification({
                    message: "ALM Deployment cancelled",
                    type: api.ToastType.Info
                }));
                await context.dispatch('getChangesets');
            } catch (err) {
                this.dispatch("ShowToastError", err);
            }
        },
        async SetALMNotificationHandler(context) {
            const handler = new ALMNotificationsHandler(context);
            context.commit("SetALMNotificationHandler", handler);
        }
    }
}

export default  AlmStore;
